import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { useAuth } from '../components/shared/AuthContext';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faLock, faEye, faEyeSlash, faShieldHalved } from "@fortawesome/free-solid-svg-icons";
import LogoName from "../assets/logoname.png";
import BgImage from "../assets/bg.png";
import Loader from '../components/ui/Loader';
import Button from "../components/ui/Button";

// Custom Temple Logo SVG component matching the reference image exactly
const TempleIcon = ({ className = "w-10 h-10" }) => (
  <svg className={className} viewBox="0 0 64 64" fill="currentColor">
    {/* Flag */}
    <rect x="31" y="1" width="2" height="6" />
    <polygon points="33,1 41,3 33,5" />
    {/* Roof */}
    <polygon points="32,7 3,25 61,25" />
    {/* Architrave */}
    <rect x="5" y="25" width="54" height="4" />
    {/* Columns */}
    <rect x="11" y="29" width="6" height="23" />
    <rect x="29" y="29" width="6" height="23" />
    <rect x="47" y="29" width="6" height="23" />
    {/* Base steps */}
    <rect x="3" y="52" width="58" height="4" />
    <rect x="1" y="56" width="62" height="4" />
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [validationError, setValidationError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Load saved credentials on mount
  useEffect(() => {
    const savedUsername = localStorage.getItem("rememberedUsername");
    const savedPassword = localStorage.getItem("rememberedPassword");
    if (savedUsername) {
      setUsername(savedUsername);
      setRememberMe(true);
    }
    if (savedPassword) {
      try {
        setPassword(atob(savedPassword));
      } catch (e) {
        localStorage.removeItem("rememberedPassword");
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Handle Remember Me
    if (rememberMe) {
      localStorage.setItem("rememberedUsername", username);
      localStorage.setItem("rememberedPassword", btoa(password));
    } else {
      localStorage.removeItem("rememberedUsername");
      localStorage.removeItem("rememberedPassword");
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/login`,
        { username, password },
        { withCredentials: true }
      );

      console.log("Full API Response:", response.data);

      if (response.data.error) {
        setValidationError(response.data.error);
        setIsLoading(false);
      } else {
        await login(response.data.token);
        setIsLoading(false);
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Login Error:", error);

      let errorMessage = "Login Failed";

      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        if (status === 401) {
          errorMessage = "Invalid username or password";
        } else if (status === 404) {
          errorMessage = "Username not found";
        } else if (status === 400) {
          errorMessage = data?.message || "Invalid username or password";
        } else if (status === 500) {
          errorMessage = "Server error - Please try again later";
        } else if (status >= 500) {
          errorMessage = "Server not responding - Please restart and try again";
        } else {
          errorMessage = data?.message || data?.error || error.message || "Login Failed";
        }
      } else if (error.request) {
        errorMessage = "Unable to connect to server - Check your connection";
      } else {
        errorMessage = error.message || "Login Failed";
      }

      setValidationError(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <div
      className="relative w-full h-screen min-h-screen flex flex-row overflow-hidden font-sans select-none bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${BgImage})` }}
    >
      {/* Loader */}
      {isLoading && <Loader message="Welcome..." overlay />}

      {/* ── Left Branding Column (Fixed, Non-Scrollable) ── */}
      <div className="hidden lg:flex lg:w-[40%] xl:w-[45%] flex-col justify-between p-12 text-white relative z-10 h-full overflow-hidden">
        {/* Top Spacer */}
        <div className="h-4" />

        {/* Main Content (Pushed to Top) */}
        <div className="max-w-md pl-12 lg:pl-16 mt-4 mb-auto">
          <h1 className="text-4xl lg:text-[42px] font-extrabold tracking-tight leading-tight mb-2 font-display whitespace-nowrap">
            Secure Banking.
          </h1>
          <h1 className="text-4xl lg:text-[42px] font-extrabold tracking-tight leading-tight mb-4 font-display whitespace-nowrap">
            Simplified Operations.
          </h1>
          {/* Decorative line */}
          <div className="w-12 h-1 bg-[#38BDF8] rounded-full my-6" />
          <p className="text-sm text-white/80 leading-relaxed font-medium max-w-xs">
            Enterprise-grade security and seamless workflows for financial institutions.
          </p>
        </div>

        {/* Company Logo and Attribution (Pushed to Bottom) */}
        <div className="pl-12 lg:pl-16 mb-6">
          <div className="flex items-center gap-3">
            <img src={LogoName} alt="Virtual Technology Logo" className="h-20 w-auto object-contain" />
            <div className="flex flex-col justify-center">
              <span className="text-[9px] text-[#38BDF8] font-bold uppercase tracking-widest leading-none">
                Developed By
              </span>
              <span className="text-sm font-bold text-white leading-none mt-1.5">
                Virtual Technology
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right Curvy Container (Width Expanded to 60%/55%) ── */}
      <div className="w-full lg:w-[60%] xl:w-[55%] bg-[#F8FAFC] lg:rounded-l-[50px] flex flex-col justify-between p-6 md:p-12 relative z-10 h-full overflow-hidden shadow-2xl">

        {/* Spacer to align vertical content */}
        <div className="hidden lg:block h-6" />

        {/* Center Container for Login Card */}
        <div className="w-full max-w-[420px] mx-auto my-auto flex flex-col justify-center">
          {/* Clean White Card */}
          <div className="bg-white rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.06)] border border-slate-100 px-8 py-10 md:px-10">

            {/* Top Circle Icon with Temple */}
            <div className="w-20 h-20 rounded-full bg-[#004BB5] flex items-center justify-center mx-auto mb-5 shadow-lg shadow-[#004BB5]/20">
              <TempleIcon className="w-10 h-10 text-white" />
            </div>

            {/* Header Titles */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-[#0F172A] tracking-tight">
                Welcome Back
              </h2>
              <p className="text-xs text-[#94A3B8] font-medium mt-1">
                Login to access your banking dashboard
              </p>
            </div>

            {/* Error Banner */}
            {validationError && (
              <div className="mb-4 flex items-start gap-2 rounded-xl px-3 py-2 border border-danger-200 bg-danger-50 text-danger text-xs font-semibold leading-snug">
                <svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="flex-1">{validationError}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">

              {/* Username Field */}
              <div>
                <label className="form-label">Username</label>
                <div className="input-group">
                  <span className="input-group-icon">
                    <FontAwesomeIcon icon={faUser} />
                  </span>
                  <input
                    type="text"
                    placeholder="Enter your username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    className="form-input pl-10"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label className="form-label">Password</label>
                <div className="input-group">
                  <span className="input-group-icon">
                    <FontAwesomeIcon icon={faLock} />
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="form-input pl-10 pr-10"
                  />
                  {password.length > 0 && (
                    <span
                      className="input-group-icon-right"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} className="text-xs" />
                    </span>
                  )}
                </div>
              </div>

              {/* Remember Me Option */}
              <div
                className="flex items-center gap-2 cursor-pointer select-none pt-1"
                onClick={() => setRememberMe(!rememberMe)}
              >
                <div
                  className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center transition-all duration-200 border ${
                    rememberMe ? "bg-primary border-primary" : "bg-white border-border"
                  }`}
                >
                  {rememberMe && (
                    <svg width="8" height="6" viewBox="0 0 11 9" fill="none">
                      <path d="M1 4L4 7L10 1" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <span className={`text-xs font-semibold transition-colors duration-200 ${
                  rememberMe ? "text-primary" : "text-ink-500"
                }`}>
                  Remember Me
                </span>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                isLoading={isLoading}
                isFullWidth
                size="xl"
                className="mt-2"
                leftIcon={<FontAwesomeIcon icon={faLock} className="text-xs" />}
              >
                Login
              </Button>
            </form>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex flex-col sm:flex-row items-center justify-between mt-6 text-[11px] text-[#94A3B8] font-medium gap-2 max-w-[420px] w-full mx-auto">
          <div className="flex items-center gap-1.5">
            <FontAwesomeIcon icon={faShieldHalved} className="text-[#3B82F6] text-xs" />
            <span>Secure. Reliable. Built for Banks.</span>
          </div>
          <span>© 2026 Jothilinga. All rights reserved.</span>
        </div>
      </div>
    </div>
  );
};

export default Login;